import { alpha } from '@mui/material/styles';
import { useDispatch } from 'react-redux';

import { DEVICE_UUID } from 'my-core/analytics/analytics-constants';
import { getVideoImage } from 'my-core/attachment-utils';
import { useStandardApiRequest } from 'my-core/hooks';
import { isMobileDevice } from 'my-core/user-agent-utils';
import { pluralize } from 'my-utils';

import Player, { AspectRatioWrapper, PlayerLoader } from 'my-components/VideoPlayer';
import ErrorMessage from 'my-elements/ErrorMessage';

import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { faFaceFrown, faHand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { toggleContactUsForm } from 'my-actions/AppStateActions';
import { fetchStudySessionRecording } from 'my-actions/StudySessionActions';

export default function StudentCoursePageStudySessionVideoPlayer({
  className,
  coverArt,
  currentUser,
  isRegistered,
  onTogglePreviewEnded,
  recording,
  studySession,
  studySessionUser,
}) {
  const fetchKey = `fetch_ssu_recording_${studySessionUser?.id}_${recording.id}`;
  const { performRequest: performFetch, requestStatus: fetchStatus } = useStandardApiRequest({
    actionCreator: fetchStudySessionRecording,
    requestKey: fetchKey,
  });
  const dispatch = useDispatch();

  const poster = getVideoImage(recording.urls.posters, 1);
  const shouldUsePreviewFile = !isRegistered && recording.urls?.drm_protected;
  const playerProps = {
    className,
    disableFullscreen: !shouldUsePreviewFile && !recording.urls?.drm_protected && isMobileDevice(), // so mobile viewers cannot view without watermark unless preview/drm protected
    elements: shouldUsePreviewFile ? undefined : recording.video_elements,
    poster,
    title: recording.name,
    trackingProps: {
      video_id: 'study_session_recording',
      is_study_session_preview: !isRegistered,
      study_session_id: studySession.id,
      filename: recording.name,
    },
    urls: shouldUsePreviewFile ? recording.urls.preview : recording.urls,
    videoDrmId: `AS:${recording.id}`,
    watermark: currentUser.email,
    fallback: <PlayerLoader className={className} poster={poster} />,
  };

  if (!isRegistered) {
    if (shouldUsePreviewFile)
      return <Player {...playerProps} onProgress={progress => progress.played >= 1 && onTogglePreviewEnded()} />;
    const startPoint = recording.video_elements?.find(e => e.type === 'start')?.time || 0;
    return (
      <Player {...playerProps} maxProgressSecs={startPoint + 5 * 60} onMaxProgressReached={onTogglePreviewEnded} />
    );
  }

  const deviceCheckRequired = !!(
    !fetchStatus?.success &&
    studySession.price &&
    !studySessionUser.device_usages?.some(u => u.uuid === DEVICE_UUID)
  );

  if (!deviceCheckRequired) {
    return <Player {...playerProps} />;
  }

  const remainingDevices = (studySessionUser.device_limit || 0) - (studySessionUser.device_usages?.length || 0);
  const maxDevicesReached = remainingDevices <= 0;

  return (
    <AspectRatioWrapper className={className}>
      {coverArt}
      <div
        css={({ palette, spacing }) => ({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          padding: spacing(2),
          overflowY: 'auto',
          backgroundColor: alpha(palette.grey[900], 0.7),
          backdropFilter: 'blur(5px)',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: palette.common.white,
        })}
      >
        {fetchStatus?.error || maxDevicesReached ?
          <>
            <FontAwesomeIcon icon={faFaceFrown} size="2x" />
            {maxDevicesReached ?
              <>
                <Typography variant="subtitle1">Recording Device Limit Reached</Typography>
                <Typography variant="body2">{`Recordings for this grade booster can only be viewed on ${pluralize(
                  studySessionUser.device_limit,
                  'device',
                )}. It looks like you have already viewed recordings for this session on ${pluralize(
                  studySessionUser.device_usages.length,
                  'other device',
                )}`}</Typography>
              </>
            : <>
                <Typography variant="subtitle1">Error Authorizing Device</Typography>
                <ErrorMessage error={fetchStatus.error} variant="body2" />
              </>
            }
            <Typography variant="body2">
              Need help with your issue?{' '}
              <MuiLink
                component="button"
                onClick={() => dispatch(toggleContactUsForm('I cannot access my grade-booster recording'))}
              >
                Contact Support
              </MuiLink>
            </Typography>
          </>
        : <>
            <FontAwesomeIcon icon={faHand} size="2x" />
            <Typography variant="subtitle1">Authorize This Device to Watch Recording</Typography>
            <Typography variant="body2">{`Recordings for this grade booster can only be viewed on ${pluralize(
              studySessionUser.device_limit,
              'unique device',
            )}. If you authorize this device, ${
              remainingDevices > 1 ?
                `you will only be able to authorize ${pluralize(remainingDevices - 1, 'additional device')}`
              : 'you cannot view from any new devices'
            }. Are you sure you’d like to continue?`}</Typography>
            <Button
              disabled={!!fetchStatus?.fetching}
              onClick={() => performFetch(studySession.id, recording.id, DEVICE_UUID, fetchKey)}
              size="small"
              variant="contained"
            >
              Start Watching
            </Button>
          </>
        }
      </div>
    </AspectRatioWrapper>
  );
}
